import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Grid,
  Paper,
  Typography,
  List,
  Button,
  Box,
  Chip,
  Tooltip,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Movie as MovieIcon,
  Theaters as TheatersIcon,
  Videocam as VideocamIcon,
  Edit as EditIcon,
  LocalMovies as LocalMoviesIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  Inventory as InventoryIcon,
  CameraAlt as CameraIcon,
  Room as RoomIcon,
  Chat as ChatIcon,
  Info,
  Close as CloseIcon,
} from '@mui/icons-material';
import { API_URL } from '../config';
import * as kanTaskService from '../services/kanTaskService';
import EditTaskModal from '../components/EditTaskModal';
import ResourceEditModal from '../components/ResourceEditModal';
import eventEmitter, { EVENTS } from '../utils/eventEmitter';

const statusLabelMap = {
  pending: 'To Do',
  'in-progress': 'In Progress',
  review: 'Review',
  completed: 'Done'
};

function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // critical/project-fetch errors (full-page)
  const [tasksError, setTasksError] = useState(null); // section-scoped
  const [resourcesError, setResourcesError] = useState(null); // section-scoped
  const [isFetchingTasks, setIsFetchingTasks] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isEditResourceModalOpen, setIsEditResourceModalOpen] = useState(false);
  const [stats, setStats] = useState({
    totalProjects: 0,
    projectsByPhase: {
      'Development': 0,
      'Pre-Production': 0,
      'Production': 0,
      'Post-Production': 0,
      'Distribution': 0
    }
  });
  const [myTasks, setMyTasks] = useState([]);
  const [allProjectTasks, setAllProjectTasks] = useState([]);
  const [taskViewMode, setTaskViewMode] = useState('mine'); // 'mine' | 'project'
  const [allResources, setAllResources] = useState([]);
  const [showPastResources, setShowPastResources] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('You need to sign in to view the dashboard.');
      navigate('/login');
      return null;
    }
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  const retryAll = () => {
    setError(null);
    setTasksError(null);
    setResourcesError(null);
    fetchDashboardData();
    fetchMyTasks();
    fetchAllResources();
  };

  // Extract the user's name from the JWT token (payload contains { id, email, name })
  const getUserName = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.name || null;
    } catch {
      return null;
    }
  };

  const userName = getUserName();
  const normalizeCrewMembersForUpdate = (crewMembers = []) =>
    (Array.isArray(crewMembers)
      ? crewMembers
          .map(member => {
            if (typeof member === 'string') return member;
            if (member?._id) return member._id;
            if (member?.user?._id) return member.user._id;
            return null;
          })
          .filter(Boolean)
      : []);

  const handleDashboardTaskUpdate = async (taskData) => {
    try {
      if (!taskData?._id) {
        console.warn('Attempted to update a task without an ID');
        return;
      }

      const payload = {
        ...taskData,
        assignees: [],
        castMembers: [],
        crewMembers: normalizeCrewMembersForUpdate(taskData.crewMembers),
      };

      delete payload.imageFilePath;

      const response = await kanTaskService.updateTask(taskData._id, payload);
      const mergedTask = {
        ...selectedTask,
        ...response,
      };

      mergedTask.displayStatus = statusLabelMap[mergedTask.status] || mergedTask.status;
      if (!mergedTask.projectName && mergedTask.project) {
        mergedTask.projectName = mergedTask.project.projectName || mergedTask.project.name;
      }

      setMyTasks(prev =>
        prev.map(task => (task._id === mergedTask._id ? { ...task, ...mergedTask } : task))
      );

      setAllProjectTasks(prev =>
        prev.map(task => (task._id === mergedTask._id ? { ...task, ...mergedTask } : task))
      );

      // Keep local selection in sync while open
      setSelectedTask(mergedTask);

      // Refresh remote data to stay aligned with backend (capturing image URL changes)
      await fetchMyTasks();
      if (taskViewMode === 'project') {
        await fetchProjectTasksForActive();
      }

      setIsEditTaskModalOpen(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('Error updating dashboard task:', err);
      setError('Failed to update task. Please try again.');
    }
  };

  useEffect(() => {
    const refreshAllData = () => {
      fetchDashboardData();
      fetchMyTasks();
      fetchAllResources();
    };

    refreshAllData();

    // Listen for resource updates coming from modals or other components
    const handleResourceUpdated = ({ resource, projectId, resourceId }) => {
      const updated = resource || { _id: resourceId, projectId };
      setAllResources(prev => prev.map(r => {
        const updatedId = updated?._id || updated?.id;
        if (!updatedId || r._id !== updatedId) return r;
        const updatedProjectId = updated.projectId;
        const preservedProjectId = typeof updatedProjectId === 'object' && updatedProjectId !== null
          ? updatedProjectId
          : r.projectId || updatedProjectId;
        const preservedProject = typeof updated.project === 'object' && updated.project !== null
          ? updated.project
          : r.project || updated.project;
        return {
          ...r,
          ...updated,
          projectId: preservedProjectId,
          project: preservedProject
        };
      }));
    };
    const handleProjectDeleted = ({ projectId }) => {
      if (!projectId) return;
      setProjects(prev => prev.filter(project => project._id !== projectId));
      setMyTasks(prev => prev.filter(task => task.projectId !== projectId && task.project?._id !== projectId && task.__project?._id !== projectId));
      setAllProjectTasks(prev => prev.filter(task => task.projectId !== projectId && task.project?._id !== projectId && task.__project?._id !== projectId));
      setAllResources(prev => prev.filter(resource => {
        const resourceProjectId = resource.projectId || resource.project?._id;
        return resourceProjectId !== projectId;
      }));
      // ensure fresh data in case other state relies on backend calculations
      refreshAllData();
    };
    eventEmitter.on && eventEmitter.on(EVENTS.RESOURCE_UPDATED, handleResourceUpdated);
    eventEmitter.on && eventEmitter.on(EVENTS.PROJECT_DELETED, handleProjectDeleted);
    return () => {
      eventEmitter.off && eventEmitter.off(EVENTS.RESOURCE_UPDATED, handleResourceUpdated);
      eventEmitter.off && eventEmitter.off(EVENTS.PROJECT_DELETED, handleProjectDeleted);
    };
  }, []);

  // When toggling to project view for the first time, lazily load project tasks
  useEffect(() => {
    if (taskViewMode === 'project' && allProjectTasks.length === 0) {
      fetchProjectTasksForActive();
    }
  }, [taskViewMode]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;
      const projectsResponse = await fetch(`${API_URL}/api/v1/projects`, { headers });
      
      if (!projectsResponse.ok) {
        throw new Error('Failed to fetch projects');
      }
      
      const projectsData = await projectsResponse.json();
      
      // Calculate phase statistics
      const phaseStats = projectsData.reduce((acc, project) => {
        acc[project.phase] = (acc[project.phase] || 0) + 1;
        return acc;
      }, {
        'Development': 0,
        'Pre-Production': 0,
        'Production': 0,
        'Post-Production': 0,
        'Distribution': 0
      });

      // Sort projects by priority and status
      const sortedProjects = [...projectsData].sort((a, b) => {
        // First sort by status (active first)
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        
        // Then sort by phase order
        const phaseOrder = {
          'Production': 0,
          'Pre-Production': 1,
          'Post-Production': 2,
          'Development': 3,
          'Distribution': 4
        };
        return phaseOrder[a.phase] - phaseOrder[b.phase];
      });

      setProjects(sortedProjects);
      setStats({
        totalProjects: projectsData.length,
        projectsByPhase: phaseStats
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyTasks = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      // First try to fetch my tasks
      const response = await fetch(`${API_URL}/api/v1/kantasks/my-tasks`, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }
      
      const tasksData = await response.json();
      
      // If we got tasks, use them
      if (tasksData && tasksData.length > 0) {
        setMyTasks(tasksData);
        // Preload project tasks in background for toggle
        fetchProjectTasksForActive().catch(() => {});
        return;
      }
      
      // If no tasks were returned, try to get all tasks for a project
      // This is a temporary solution until the my-tasks endpoint is fixed
      const projectsResponse = await fetch(`${API_URL}/api/v1/projects`, { headers });
      
      if (!projectsResponse.ok) {
        throw new Error('Failed to fetch projects for task fallback');
      }
      
      const projectsData = await projectsResponse.json();
      
      // If we have projects, get tasks for the first active project
      if (projectsData && projectsData.length > 0) {
        const activeProject = projectsData.find(p => p.status === 'active') || projectsData[0];
        
        const projectTasksResponse = await fetch(`${API_URL}/api/v1/kantasks/project/${activeProject._id}`, { headers });
        
        if (!projectTasksResponse.ok) {
          throw new Error('Failed to fetch project tasks as fallback');
        }
        
        const projectTasksData = await projectTasksResponse.json();
        // Format the tasks from the project endpoint
        const allTasks = [
          ...(projectTasksData.pending || []),
          ...(projectTasksData['in-progress'] || []),
          ...(projectTasksData.review || []),
          ...(projectTasksData.completed || [])
        ];
        
        // Get current user info from localStorage
        const token = localStorage.getItem('token');
        let userId = null;
        
        if (token) {
          try {
            // Extract user ID from token (assuming JWT)
            const tokenData = JSON.parse(atob(token.split('.')[1]));
            userId = tokenData.id || tokenData._id || tokenData.userId;
          } catch (e) {
            console.error('Error extracting user ID from token:', e);
          }
        }
        
        // Filter tasks to only show those created by or assigned to the current user
        const userTasks = allTasks.filter(task => {
          // Check if user is in watchers (creator)
          const isWatcher = task.watchers && task.watchers.some(watcher => {
            const watcherId = typeof watcher === 'string' ? watcher : 
                           (watcher && watcher._id) ? watcher._id : null;
            return watcherId === userId;
          });
          
          // Check if user is in crewMembers (assigned)
          const isCrewMember = task.crewMembers && task.crewMembers.some(crew => {
            const crewId = typeof crew === 'string' ? crew : 
                         (crew && crew._id) ? crew._id : null;
            return crewId === userId;
          });
          
          return isWatcher || isCrewMember;
        });
        
        // Map the status values to user-friendly formats
        const formattedTasks = userTasks.map(task => ({
          ...task,
          displayStatus: statusLabelMap[task.status] || task.status,
          projectName: activeProject.projectName || 'Project'
        }));
        
        setMyTasks(formattedTasks);
        // Also set all project tasks for toggling
        const formattedAll = allTasks.map(t => ({
          ...t,
          displayStatus: statusLabelMap[t.status] || t.status,
          projectName: activeProject.projectName || 'Project'
        }));
        setAllProjectTasks(formattedAll);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasksError('Unable to load your tasks. Please try again.');
    }
  };

  // Fetch tasks for the active project (unfiltered) to support toggle view
  const fetchProjectTasksForActive = async () => {
    setIsFetchingTasks(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      // Fetch all projects accessible to the user (created by or invited)
      const projectsResponse = await fetch(`${API_URL}/api/v1/projects`, { headers });
      if (!projectsResponse.ok) return;
      const projectsData = await projectsResponse.json();
      if (!projectsData || projectsData.length === 0) return;

      // Fetch tasks for each project in parallel
      const tasksByProject = await Promise.all(
        projectsData.map(async (proj) => {
          try {
            const res = await fetch(`${API_URL}/api/v1/kantasks/project/${proj._id}`, { headers });
            if (!res.ok) return [];
            const data = await res.json();
            const merged = [
              ...(data.pending || []),
              ...(data['in-progress'] || []),
              ...(data.review || []),
              ...(data.completed || [])
            ];
            return merged.map(t => ({ ...t, __project: proj }));
          } catch (projErr) {
            console.error(`Error fetching tasks for project ${proj._id}:`, projErr);
            return [];
          }
        })
      );

      const flat = tasksByProject.flat();
      const formattedAll = flat.map(t => ({
        ...t,
        displayStatus: statusLabelMap[t.status] || t.status,
        projectName: (t.__project?.projectName) || t.projectName || 'Project',
        projectId: t.projectId || t.__project?._id || t.project?._id,
      }));
      setAllProjectTasks(formattedAll);
    } catch (e) {
      console.error('Error fetching project tasks for toggle:', e);
    } finally {
      setIsFetchingTasks(false);
    }
  };

  const fetchAllResources = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await fetch(`${API_URL}/api/v1/resources`, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to fetch resources');
      }
      
      const data = await response.json();
      setAllResources(data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResourcesError('Failed to load resources. Please try again.');
    }
  };

  const getCurrentResources = (resources) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return resources.filter(resource => {
      const startDate = new Date(resource.startDate);
      const endDate = new Date(resource.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      // Show any resource that is active now or in the future; hide only past/denied
      const isPast = endDate < today;
      const isDenied = resource.status === 'denied';

      return !isPast && !isDenied;
    });
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'Development': return <EditIcon />;
      case 'Pre-Production': return <MovieIcon />;
      case 'Production': return <VideocamIcon />;
      case 'Post-Production': return <LocalMoviesIcon />;
      case 'Distribution': return <TheatersIcon />;
      default: return <MovieIcon />;
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase?.toLowerCase()) {
      case 'development':
        return theme.palette.warning.main;
      case 'pre-production':
        return theme.palette.success.main;
      case 'production':
        return theme.palette.primary.main;
      case 'post-production':
        return theme.palette.secondary.main;
      case 'distribution':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Format date without timezone shift to ensure accurate date display
  const formatDateWithoutTimezoneShift = (dateString) => {
    if (!dateString) return '';
    
    // Parse the date parts directly to avoid timezone issues
    const [year, month, day] = dateString.split('T')[0].split('-').map(num => parseInt(num, 10));
    
    // Create a formatted date string (month is 0-indexed in JS Date)
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  // Determine whether a date string is in the past.
  // Compares against the end of "today" so a due date / end date of today
  // is NOT flagged as overdue (consistent with formatDateWithoutTimezoneShift,
  // which displays such dates as today).
  const isDatePast = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return false;
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    return date < endOfToday;
  };

  // Resolve the phase for a task. Tasks don't carry a `phase` field directly;
  // it lives on the associated project. Falls back to 'Development' so the
  // colored accent bar always renders a sensible color.
  const getTaskPhase = (task) => {
    if (!task) return 'Development';
    return task.phase || task.project?.phase || task.__project?.phase || 'Development';
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'Studio': return <RoomIcon />;
      case 'Equipment': return <CameraIcon />;
      case 'Post-Production': return <LocalMoviesIcon />;
      case 'Meeting Room': return <GroupIcon />;
      default: return <InventoryIcon />;
    }
  };

  // Resolve project name for a resource whether projectId is populated object or plain id
  const getResourceProjectName = (resource) => {
    if (!resource) return 'Unassigned';
    const pid = resource.projectId || resource.project;
    if (pid && typeof pid === 'object') {
      return pid.projectName || pid.name || 'Unassigned';
    }
    if (typeof pid === 'string') {
      const proj = projects.find(p => p._id === pid);
      if (proj) return proj.projectName || proj.name || 'Unassigned';
    }
    if (resource.projectName) return resource.projectName;
    if (resource.projectTitle) return resource.projectTitle;
    return 'Unassigned';
  };

  // Memoize derived data so we don't re-filter/re-compute on every render
  const currentResources = useMemo(() => getCurrentResources(allResources), [allResources]);
  const visibleTaskCount = taskViewMode === 'mine' ? myTasks.length : allProjectTasks.length;
  const visibleTasks = taskViewMode === 'mine' ? myTasks : allProjectTasks;
  const activeProjects = useMemo(
    () => projects.filter(project => project.phase !== 'Distribution'),
    [projects]
  );

  if (error) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 2 }}>
        <Typography color="error" sx={{ fontSize: '1.1rem' }}>{error}</Typography>
        <Button variant="contained" color="primary" onClick={retryAll} aria-label="retry loading dashboard">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1.5, sm: 2, md: 2.5 }, 
      width: '100%',
      maxWidth: 'none',
      mx: 0,
      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.94) : '#f8fafc',
      transition: 'background-color 0.3s ease',
      minHeight: '100vh'
    }}>
      {/* Professional Welcome Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          borderRadius: 0,
          backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : '#e5eaf1',
          color: theme.palette.mode === 'dark' ? 'white' : theme.palette.text.primary,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `1px solid ${alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.15)}`,
        }}
      >
        <Typography variant="overline" sx={{ 
          color: theme.palette.primary.main,
          fontWeight: 700,
          letterSpacing: 3,
          fontSize: '0.7rem',
          mb: 0.5,
          display: 'block'
        }}>
          PRODUCTION DASHBOARD
        </Typography>
        <Typography variant="h4" sx={{ 
          fontWeight: 700, 
          mb: 1,
          fontSize: { xs: '1.25rem', md: '1.5rem' },
          letterSpacing: '-0.02em',
          color: theme.palette.text.primary
        }}>
          {userName ? `Welcome back, ${userName.split(' ')[0]}` : 'Welcome Back'}
        </Typography>
        <Typography variant="body1" sx={{
          color: theme.palette.text.secondary,
          maxWidth: 600
        }}>
          {stats.totalProjects > 0
            ? `${stats.totalProjects} ${stats.totalProjects === 1 ? 'project' : 'projects'} across your production pipeline`
            : 'Your production pipeline will come to life once you create your first project'}
        </Typography>
      </Paper>

      {/* Production Pipeline */}
      <Box sx={{ mb: { xs: 2, md: 2.5 } }}>
        <Typography variant="overline" sx={{ 
          color: theme.palette.text.secondary,
          fontWeight: 600,
          letterSpacing: 2,
          fontSize: '0.7rem',
          mb: 2,
          display: 'block'
        }}>
          PRODUCTION PIPELINE
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(stats.projectsByPhase).map(([phase, count], index) => (
            <Grid item xs={6} sm={4} md={2.4} key={phase}>
              {loading ? (
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
              ) : (
                <Tooltip title={`${count} in ${phase}`} enterDelay={400}>
                  <Paper 
                    elevation={0}
                    tabIndex={0}
                    role="button"
                    aria-label={`View ${phase} projects`}
                    onClick={() => navigate(`/projects?phase=${encodeURIComponent(phase)}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/projects?phase=${encodeURIComponent(phase)}`); } }}
                    sx={{ 
                    p: 1.25,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    borderRadius: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    border: theme.palette.mode === 'dark' ?
                      `1px solid ${alpha(theme.palette.common.white, 0.08)}` :
                      `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
                    background: theme.palette.mode === 'dark' ?
                      `linear-gradient(145deg, ${alpha('#f5f5f5', 0.1)}, ${alpha('#e0e0e0', 0.05)})` :
                      `linear-gradient(145deg, ${alpha('#f5f5f5', 0.8)}, ${alpha('#e0e0e0', 0.6)})`,
                    backdropFilter: 'blur(10px)',
                    boxShadow: theme.palette.mode === 'dark' ?
                      `0 4px 14px ${alpha(theme.palette.common.black, 0.25)}` :
                      `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: theme.palette.mode === 'dark' ?
                        `0 10px 28px ${alpha(theme.palette.common.black, 0.4)}` :
                        `0 8px 22px ${alpha(theme.palette.common.black, 0.12)}`
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${alpha(theme.palette.primary.main, 0.8)}`,
                      outlineOffset: 2
                    },
                    '&::before': count > 0 ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      background: getPhaseColor(phase)
                    } : {}
                    }}
                  >
                  <Box 
                    sx={{ 
                      color: getPhaseColor(phase), 
                      mb: 1,
                      backgroundColor: alpha(getPhaseColor(phase), 0.08),
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: `2px solid ${alpha(getPhaseColor(phase), 0.9)}`,
                    }}
                  >
                    {React.cloneElement(getPhaseIcon(phase), { fontSize: 'small' })}
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 0.25, 
                      fontWeight: 700,
                      color: count > 0 ? 'text.primary' : 'text.secondary', 
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      letterSpacing: '-0.2px'
                    }}
                  >
                    {count}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.secondary',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase'
                    }}
                  >
                    {phase}
                  </Typography>
                  </Paper>
                </Tooltip>
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
      


      {/* Tasks and Resources Overview Row */}
      {/* Title removed per user request */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* My Tasks - Enhanced */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: 560,
              borderRadius: 2,
              overflow: 'hidden',
              border: theme.palette.mode === 'dark' ? 
                `1px solid ${alpha(theme.palette.common.white, 0.1)}` : 
                `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
              background: theme.palette.mode === 'dark' ?
                `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.7)})` :
                `linear-gradient(145deg, ${alpha('#fff', 0.95)}, ${alpha('#fff', 1)})`,
              backdropFilter: 'blur(10px)',
              boxShadow: theme.palette.mode === 'dark' ?
                `0 4px 14px ${alpha(theme.palette.common.black, 0.25)}` :
                `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: theme.palette.mode === 'dark' ?
                  `0 6px 18px ${alpha(theme.palette.common.black, 0.35)}` :
                  `0 4px 12px ${alpha(theme.palette.common.black, 0.12)}`,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2,
                borderBottom: theme.palette.mode === 'dark' ? 
                  `1px solid ${alpha(theme.palette.common.white, 0.08)}` : 
                  `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
                background: theme.palette.mode === 'dark' ?
                  `linear-gradient(to right, ${alpha(theme.palette.primary.dark, 0.2)}, ${alpha(theme.palette.background.paper, 0.2)})` :
                  `linear-gradient(to right, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(theme.palette.primary.main, 0.03)})`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  }}
                >
                  <ScheduleIcon sx={{ color: theme.palette.secondary.main, fontSize: 18 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  My Tasks
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={taskViewMode}
                  onChange={(e, val) => { if (val) setTaskViewMode(val); }}
                  sx={{
                    background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.primary.main, 0.06),
                    borderRadius: 1,
                    p: 0.25,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      px: 1,
                      py: 0.25,
                      border: 'none',
                    }
                  }}
                >
                  <ToggleButton value="mine" aria-label="show my tasks">Mine</ToggleButton>
                  <ToggleButton value="project" aria-label="show all project tasks">All Projects</ToggleButton>
                </ToggleButtonGroup>
                <Chip 
                  label={`${visibleTaskCount} ${visibleTaskCount === 1 ? 'task' : 'tasks'}`} 
                  size="small"
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderRadius: 1,
                    background: theme.palette.mode === 'dark' ?
                      `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.7)}, ${alpha(theme.palette.primary.main, 0.4)})` :
                      `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.8)}, ${alpha(theme.palette.primary.main, 0.3)})`,
                    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.dark,
                    border: theme.palette.mode === 'dark' ?
                      `1px solid ${alpha(theme.palette.primary.main, 0.3)}` :
                      'none',
                    boxShadow: theme.palette.mode === 'dark' ?
                      `0 2px 8px ${alpha(theme.palette.common.black, 0.25)}` :
                      `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }}
                />
              </Box>
            </Box>
            {loading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : tasksError ? (
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2, color: 'text.secondary' }}>
                <Typography color="error" sx={{ textAlign: 'center' }}>{tasksError}</Typography>
                <Button size="small" variant="outlined" onClick={() => { setTasksError(null); fetchMyTasks(); }}>
                  Retry
                </Button>
              </Box>
            ) : (
              <Box sx={{ overflow: 'auto', flexGrow: 1, px: 1.5, py: 1.5 }}>
                {(taskViewMode === 'project' && isFetchingTasks && visibleTaskCount === 0) ? (
                  <Box sx={{ px: 1 }}>
                    {[...Array(4)].map((_, i) => (
                      <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 1, mb: 1.5 }} />
                    ))}
                  </Box>
                ) : visibleTaskCount > 0 ? (
                  <List sx={{ p: 0 }}>
                    {visibleTasks.map((task) => {
                      const isOverdue = isDatePast(task.dueDate);
                      return (
                      <Paper
                        key={task._id}
                        elevation={0}
                        role="button"
                        tabIndex={0}
                        aria-label={`Edit task: ${task.title}`}
                        sx={{
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 1,
                              background: theme.palette.mode === 'dark' ?
                            `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.35)}, ${alpha(theme.palette.background.paper, 0.15)})` :
                            `linear-gradient(145deg, ${alpha('#ffffff', 0.6)}, ${alpha('#ffffff', 0.85)})`,
                          border: theme.palette.mode === 'dark' ?
                            `1px solid ${alpha(theme.palette.common.white, 0.1)}` :
                            `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          backdropFilter: 'blur(10px)',
                          boxShadow: theme.palette.mode === 'dark' ?
                            `0 4px 14px ${alpha(theme.palette.common.black, 0.25)}` :
                            `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                          '&:hover': {
                            boxShadow: theme.palette.mode === 'dark' ?
                              `0 6px 18px ${alpha(theme.palette.common.black, 0.35)}` :
                              `0 4px 12px ${alpha(theme.palette.common.black, 0.12)}`,
                            borderColor: theme.palette.mode === 'dark' ?
                              alpha(theme.palette.primary.main, 0.4) :
                              alpha(theme.palette.primary.main, 0.25),
                            transform: 'translateY(-1px)'
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: 5,
                            background: `linear-gradient(to bottom, ${getPhaseColor(getTaskPhase(task))}, ${alpha(getPhaseColor(getTaskPhase(task)), 0.7)})`,
                            borderTopLeftRadius: 8,
                            borderBottomLeftRadius: 8,
                            boxShadow: `0 0 8px ${alpha(getPhaseColor(getTaskPhase(task)), 0.4)}`
                          }
                        }}
                        onClick={() => {
                          setSelectedTask(task);
                          setIsEditTaskModalOpen(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedTask(task);
                            setIsEditTaskModalOpen(true);
                          }
                        }}
                      >
                        <Box sx={{ pl: 1.5 }}>
                          {/* Task Header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '75%' }}>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 700,
                                  fontSize: '1rem',
                                  letterSpacing: '-0.2px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: theme.palette.mode === 'dark' ? 
                                    theme.palette.common.white : 
                                    theme.palette.text.primary
                                }}
                              >
                                {task.title}
                              </Typography>
                              {task.messages && task.messages.length > 0 && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    color: theme.palette.mode === 'dark' ? 
                                      theme.palette.common.white : 
                                      theme.palette.primary.main,
                                    background: theme.palette.mode === 'dark' ?
                                      `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.7)}, ${alpha(theme.palette.primary.main, 0.4)})` :
                                      alpha(theme.palette.primary.main, 0.1),
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.3,
                                    ml: 1,
                                    boxShadow: theme.palette.mode === 'dark' ?
                                      `0 2px 8px ${alpha(theme.palette.common.black, 0.25)}` :
                                      'none'
                                  }}
                                >
                                  <ChatIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    {task.messages.length}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Chip
                              size="small"
                              label={task.displayStatus || task.status}
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: '8px',
                                px: 0.5,
                                background: theme.palette.mode === 'dark' ?
                                  (task.status === 'completed' || task.status === 'done' ? 
                                    `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.7)}, ${alpha(theme.palette.success.main, 0.4)})` :
                                   task.status === 'in-progress' ? 
                                    `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.7)}, ${alpha(theme.palette.primary.main, 0.4)})` :
                                   task.status === 'review' ? 
                                    `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.7)}, ${alpha(theme.palette.secondary.main, 0.4)})` :
                                    `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.7)}, ${alpha(theme.palette.warning.main, 0.4)})`) :
                                  (task.status === 'completed' || task.status === 'done' ? alpha(theme.palette.success.main, 0.1) :
                                   task.status === 'in-progress' ? alpha(theme.palette.primary.main, 0.1) :
                                   task.status === 'review' ? alpha(theme.palette.secondary.main, 0.1) :
                                   alpha(theme.palette.warning.main, 0.1)),
                                color: theme.palette.mode === 'dark' ? 
                                  '#ffffff' :
                                  (task.status === 'completed' || task.status === 'done' ? theme.palette.success.main :
                                   task.status === 'in-progress' ? theme.palette.primary.main :
                                   task.status === 'review' ? theme.palette.secondary.main :
                                   theme.palette.warning.main),
                                border: theme.palette.mode === 'dark' ?
                                  (task.status === 'completed' || task.status === 'done' ? 
                                    `1px solid ${alpha(theme.palette.success.main, 0.3)}` :
                                   task.status === 'in-progress' ? 
                                    `1px solid ${alpha(theme.palette.primary.main, 0.3)}` :
                                   task.status === 'review' ? 
                                    `1px solid ${alpha(theme.palette.secondary.main, 0.3)}` :
                                    `1px solid ${alpha(theme.palette.warning.main, 0.3)}`) :
                                  'none',
                                boxShadow: theme.palette.mode === 'dark' ?
                                  `0 2px 8px ${alpha(theme.palette.common.black, 0.25)}` :
                                  'none'
                              }}
                            />
                          </Box>
                          
                          {/* Project Name */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 1.5,
                              color: theme.palette.mode === 'dark' ? 
                                alpha(theme.palette.common.white, 0.7) : 
                                theme.palette.text.secondary,
                              fontSize: '0.87rem',
                              fontWeight: 500
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                mr: 1,
                                background: theme.palette.mode === 'dark' ?
                                  alpha(theme.palette.primary.dark, 0.3) :
                                  alpha(theme.palette.primary.main, 0.1),
                              }}
                            >
                              <MovieIcon sx={{ 
                                fontSize: '0.9rem', 
                                color: theme.palette.mode === 'dark' ? 
                                  alpha(theme.palette.primary.light, 0.9) : 
                                  theme.palette.primary.main 
                              }} />
                            </Box>
                            {
                              // First try to use project object with ID for navigation
                              task.project && task.project._id ? (
                                <Box 
                                  component="span" 
                                  sx={{ 
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/projects/${task.project._id}`);
                                  }}
                                >
                                  {/* Try different ways to get the project name */}
                                  {task.project.projectName || task.project.name || task.projectName || 'Project'}
                                </Box>
                              ) : 
                              // If no project object with ID, try to use projectId
                              task.projectId ? (
                                <Box 
                                  component="span" 
                                  sx={{ 
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/projects/${task.projectId}`);
                                  }}
                                >
                                  {task.projectName || 'Project'}
                                </Box>
                              ) : 
                              // If no project reference at all, just show the name
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {task.projectName || 'Unknown Project'}
                              </Typography>
                            }
                          </Box>
                          
                          {/* Task Dates */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              color: theme.palette.mode === 'dark' ? 
                                alpha(theme.palette.common.white, 0.7) : 
                                theme.palette.text.secondary,
                              fontSize: '0.85rem',
                              fontWeight: 500
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                mr: 1,
                                background: theme.palette.mode === 'dark' ?
                                  alpha(theme.palette.info.dark, 0.3) :
                                  alpha(theme.palette.info.main, 0.1),
                              }}
                            >
                              <ScheduleIcon sx={{ 
                                fontSize: '0.9rem', 
                                color: theme.palette.mode === 'dark' ? 
                                  alpha(theme.palette.info.light, 0.9) : 
                                  theme.palette.info.main 
                              }} />
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {task.startDate && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    bgcolor: alpha(theme.palette.divider, 0.3),
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.3
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7 }}>
                                    Start:
                                  </Typography>
                                  <Typography variant="caption">
                                    {formatDateWithoutTimezoneShift(task.startDate)}
                                  </Typography>
                                </Box>
                              )}
                              
                              {task.endDate && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    bgcolor: alpha(theme.palette.divider, 0.3),
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.3
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7 }}>
                                    End:
                                  </Typography>
                                  <Typography variant="caption">
                                    {formatDateWithoutTimezoneShift(task.endDate)}
                                  </Typography>
                                </Box>
                              )}
                              
                              {task.dueDate && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    bgcolor: isOverdue ? alpha('#f44336', 0.1) : alpha(theme.palette.divider, 0.3),
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.3,
                                    color: isOverdue ? '#f44336' : 'inherit'
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 600, opacity: isOverdue ? 1 : 0.7 }}>
                                    Due:
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: isOverdue ? 600 : 400 }}>
                                    {formatDateWithoutTimezoneShift(task.dueDate)}
                                  </Typography>
                                </Box>
                              )}
                              
                              {!task.startDate && !task.endDate && !task.dueDate && (
                                <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                  No dates specified
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                      );
                    })}
                  </List>
                ) : (
                  <Box 
                    sx={{ 
                      py: 4, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary'
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: '2.5rem', mb: 2, opacity: 0.3 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {taskViewMode === 'mine' ? 'No tasks assigned to you' : 'No tasks in this project'}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: '80%' }}>
                      {taskViewMode === 'mine' ? 'Tasks assigned to you will appear here. Visit a project to create new tasks.' : 'Project tasks will appear here. Visit a project to create new tasks.'}
                    </Typography>
                    <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/projects')} aria-label="go to projects">
                      Go to Projects
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Resource Overview - Enhanced with Glass Effect */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: 560,
              borderRadius: 2,
              overflow: 'hidden',
              border: theme.palette.mode === 'dark' ?
                `1px solid ${alpha(theme.palette.common.white, 0.1)}` :
                `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
              background: theme.palette.mode === 'dark' ?
                `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.7)})` :
                `linear-gradient(145deg, ${alpha('#fff', 0.95)}, ${alpha('#fff', 1)})`,
              backdropFilter: 'blur(10px)',
              boxShadow: theme.palette.mode === 'dark' ?
                `0 4px 14px ${alpha(theme.palette.common.black, 0.25)}` :
                `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: theme.palette.mode === 'dark' ?
                  `0 6px 18px ${alpha(theme.palette.common.black, 0.35)}` :
                  `0 4px 12px ${alpha(theme.palette.common.black, 0.12)}`,
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2,
                borderBottom: theme.palette.mode === 'dark' ? 
                  `1px solid ${alpha(theme.palette.common.white, 0.08)}` : 
                  `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
                background: theme.palette.mode === 'dark' ?
                  `linear-gradient(to right, ${alpha(theme.palette.primary.dark, 0.2)}, ${alpha(theme.palette.background.paper, 0.2)})` :
                  `linear-gradient(to right, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(theme.palette.primary.main, 0.03)})`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  }}
                >
                  <InventoryIcon sx={{ color: theme.palette.secondary.main, fontSize: 18 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  Resource Overview
                </Typography>
              </Box>
              <Chip 
                label={`${currentResources.length} ${currentResources.length === 1 ? 'resource' : 'resources'}`} 
                size="small"
                sx={{ 
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  borderRadius: 1,
                  border: 'none'
                }}
              />
            </Box>
            {loading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : resourcesError ? (
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2, color: 'text.secondary' }}>
                <Typography color="error" sx={{ textAlign: 'center' }}>{resourcesError}</Typography>
                <Button size="small" variant="outlined" onClick={() => { setResourcesError(null); fetchAllResources(); }}>
                  Retry
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        Resource Status
                      </Typography>
                      <Tooltip title="Resources currently available or scheduled">
                        <Info sx={{ fontSize: '0.9rem', color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => setShowPastResources(!showPastResources)}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 1,
                        px: 2,
                        color: theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      startIcon={showPastResources ? <CloseIcon /> : <ScheduleIcon />}
                    >
                      {showPastResources ? 'Hide Past Resources' : 'Show Past Resources'}
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box 
                      sx={{ 
                        py: 0.3,
                        px: 1,
                        borderRadius: 1, 
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexGrow: 1,
                        maxWidth: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.secondary.main, lineHeight: 1, fontSize: '0.8rem' }}>
                        {currentResources.filter(r => r.status === 'pending_approval').length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem' }}>
                        Pending
                      </Typography>
                    </Box>
                    
                    <Box 
                      sx={{ 
                        py: 0.3,
                        px: 1,
                        borderRadius: 1, 
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexGrow: 1,
                        maxWidth: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.success.main, lineHeight: 1, fontSize: '0.8rem' }}>
                        {currentResources.filter(r => r.status === 'accepted').length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem' }}>
                        Accepted
                      </Typography>
                    </Box>
                    
                    <Box 
                      sx={{ 
                        py: 0.3,
                        px: 1,
                        borderRadius: 1, 
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexGrow: 1,
                        maxWidth: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.warning.main, lineHeight: 1, fontSize: '0.8rem' }}>
                        {currentResources.filter(r => r.status === 'maintenance').length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Maint.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ overflow: 'auto', flexGrow: 1, px: 1.5, py: 1.5 }}>
                  {allResources.length > 0 ? (
                    <List sx={{ p: 0 }}>
                      {(showPastResources ? allResources : currentResources).map((resource) => {
                        const isOverdue = isDatePast(resource.endDate);
                        return (
                        <Paper
                          key={resource._id}
                          elevation={0}
                          role="button"
                          tabIndex={0}
                          aria-label={`Edit resource: ${resource.name}`}
                          sx={{
                            mb: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            background: theme.palette.mode === 'dark' ?
                              `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.35)}, ${alpha(theme.palette.background.paper, 0.15)})` :
                              `linear-gradient(145deg, ${alpha('#ffffff', 0.6)}, ${alpha('#ffffff', 0.85)})`,
                            border: theme.palette.mode === 'dark' ?
                              `1px solid ${alpha(theme.palette.common.white, 0.1)}` :
                              `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            opacity: isOverdue ? 0.8 : 1,
                            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: theme.palette.mode === 'dark' ?
                              `0 6px 22px ${alpha(theme.palette.common.black, 0.3)}` :
                              `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
                            '&:hover': {
                              boxShadow: theme.palette.mode === 'dark' ?
                                `0 10px 28px ${alpha(theme.palette.common.black, 0.4)}` :
                                `0 8px 20px ${alpha(theme.palette.common.black, 0.14)}`,
                              borderColor: theme.palette.mode === 'dark' ?
                                alpha(theme.palette.secondary.main, 0.4) :
                                alpha(theme.palette.secondary.main, 0.25),
                              transform: 'translateY(-3px)'
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              bottom: 0,
                              width: 4,
                              backgroundColor: 
                                resource.status === 'pending_approval' ? '#9c27b0' : 
                                resource.status === 'accepted' ? '#4caf50' : 
                                resource.status === 'denied' ? '#f44336' :
                                '#ff9800',
                              borderTopLeftRadius: 8,
                              borderBottomLeftRadius: 8
                            }
                          }}
                          onClick={() => {
                            setSelectedResource(resource);
                            setIsEditResourceModalOpen(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedResource(resource);
                              setIsEditResourceModalOpen(true);
                            }
                          }}
                        >
                          <Box sx={{ pl: 1.5 }}>
                            {/* Resource Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, maxWidth: '75%' }}>
                                <Box 
                                  sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '8px',
                                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                                  }}
                                >
                                  {getResourceIcon(resource.type)}
                                </Box>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {resource.name}
                                </Typography>
                                {isOverdue && (
                                  <Chip
                                    size="small"
                                    label="Past Schedule"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.7rem',
                                      height: 20,
                                      borderRadius: '4px',
                                      bgcolor: alpha(theme.palette.grey[500], 0.1),
                                      color: theme.palette.text.secondary,
                                      border: 'none'
                                    }}
                                  />
                                )}
                              </Box>
                              <Chip
                                size="small"
                                label={
                                  resource.status === 'pending_approval' ? 'Pending Approval' :
                                  resource.status === 'accepted' ? 'Accepted' :
                                  resource.status === 'denied' ? 'Denied' :
                                  resource.status === 'maintenance' ? 'Maintenance' :
                                  resource.status
                                }
                                sx={{
                                  fontWeight: 600,
                                  borderRadius: '6px',
                                  bgcolor: 
                                    resource.status === 'pending_approval' ? alpha('#9c27b0', 0.1) :
                                    resource.status === 'accepted' ? alpha('#4caf50', 0.1) :
                                    resource.status === 'denied' ? alpha('#f44336', 0.1) :
                                    alpha('#ff9800', 0.1),
                                  color: 
                                    resource.status === 'pending_approval' ? '#9c27b0' :
                                    resource.status === 'accepted' ? '#4caf50' :
                                    resource.status === 'denied' ? '#f44336' :
                                    '#ff9800',
                                  border: 'none'
                                }}
                              />
                            </Box>
                            
                            {/* Resource Type and Project */}
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: 1.5,
                                color: 'text.secondary',
                                fontSize: '0.85rem'
                              }}
                            >
                              <MovieIcon sx={{ fontSize: '0.95rem', mr: 1, opacity: 0.7 }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {getResourceProjectName(resource)}
                              </Typography>
                              <Box 
                                sx={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  ml: 2,
                                  pl: 2,
                                  borderLeft: `1px solid ${alpha(theme.palette.divider, 0.8)}`
                                }}
                              >
                                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                                  <Box component="span" sx={{ opacity: 0.7, mr: 0.5 }}>Type:</Box> {resource.type}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {/* Resource Dates */}
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                color: 'text.secondary',
                                fontSize: '0.8rem'
                              }}
                            >
                              <ScheduleIcon sx={{ fontSize: '0.9rem', mr: 1, opacity: 0.7 }} />
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 0.5,
                                  bgcolor: alpha(theme.palette.divider, 0.3),
                                  borderRadius: '4px',
                                  px: 1,
                                  py: 0.3
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7 }}>
                                  From:
                                </Typography>
                                <Typography variant="caption">
                                  {formatDateWithoutTimezoneShift(resource.startDate)}
                                </Typography>
                              </Box>
                              <Box 
                                component="span" 
                                sx={{ 
                                  mx: 0.5, 
                                  color: alpha(theme.palette.text.secondary, 0.5)
                                }}
                              >
                                —
                              </Box>
                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 0.5,
                                  bgcolor: isOverdue ? alpha('#f44336', 0.1) : alpha(theme.palette.divider, 0.3),
                                  borderRadius: '4px',
                                  px: 1,
                                  py: 0.3,
                                  color: isOverdue ? '#f44336' : 'inherit'
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, opacity: isOverdue ? 1 : 0.7 }}>
                                  To:
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: isOverdue ? 600 : 400 }}>
                                  {formatDateWithoutTimezoneShift(resource.endDate)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                        );
                      })}
                    </List>
                  ) : (
                    <Box 
                      sx={{ 
                        py: 6, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        color: 'text.secondary'
                      }}
                    >
                      <InventoryIcon sx={{ fontSize: '3rem', mb: 2, opacity: 0.3 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                        No resources available
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: '80%' }}>
                        Resources you request or are assigned to you will appear here.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Current Projects - Enhanced */}
      <Typography variant="overline" sx={{ 
        color: theme.palette.text.secondary,
        fontWeight: 600,
        letterSpacing: 2,
        fontSize: '0.7rem',
        mb: 2,
        display: 'block'
      }}>
        CURRENT PROJECTS
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        {loading ? (
          <Grid container spacing={2}>
            {[...Array(3)].map((_, index) => (
              <Grid item xs={12} sm={6} md={3} lg={2} key={index}>
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : activeProjects.length > 0 ? (
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {activeProjects
              .map(project => (
                <Grid item xs={12} sm={6} md={3} lg={2} key={project._id}>
                  <Paper
                    elevation={0}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${project.projectName}`}
                    sx={{
                      borderRadius: 1,
                      overflow: 'hidden',
                      height: '100%',
                      border: theme.palette.mode === 'dark' ? 
                        `1px solid ${alpha(theme.palette.common.white, 0.1)}` : 
                        `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                      background: theme.palette.mode === 'dark' ?
                        `linear-gradient(145deg, ${alpha('#f5f5f5', 0.1)}, ${alpha('#e0e0e0', 0.05)})` :
                        `linear-gradient(145deg, ${alpha('#f5f5f5', 0.8)}, ${alpha('#e0e0e0', 0.6)})`,
                      backdropFilter: 'blur(10px)',
                      boxShadow: theme.palette.mode === 'dark' ?
                        '0 2px 8px rgba(0,0,0,0.2)' :
                        '0 1px 4px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: theme.palette.mode === 'dark' ?
                          '0 8px 24px rgba(0,0,0,0.3)' :
                          '0 4px 12px rgba(0,0,0,0.12)'
                      },
                      position: 'relative'
                    }}
                    onClick={() => navigate(`/projects/${project._id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/projects/${project._id}`); } }}
                  >
                    {/* Color Bar at Top */}
                    <Box 
                      sx={{ 
                        height: 3, 
                        width: '100%', 
                        bgcolor: getPhaseColor(project.phase),
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1
                      }} 
                    />
                    
                    {/* Project Header */}
                    <Box sx={{ p: { xs: 1, sm: 1.25 }, pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700, 
                            fontSize: '0.9rem',
                            lineHeight: 1.25,
                            maxWidth: '75%',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {project.projectName}
                        </Typography>
                        <Chip 
                          size="small"
                          label={project.phase}
                          sx={{ 
                            bgcolor: alpha(getPhaseColor(project.phase), 0.1),
                            color: getPhaseColor(project.phase),
                            fontWeight: 600,
                            borderRadius: 1,
                            border: `1px solid ${alpha(getPhaseColor(project.phase), 0.2)}`,
                            height: 18,
                            fontSize: '0.65rem'
                          }}
                        />
                      </Box>

                      {/* Status dot + project type + collaborator count */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: project.status === 'active' ? theme.palette.success.main :
                                       project.status === 'on-hold' ? theme.palette.warning.main :
                                       theme.palette.grey[500],
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'capitalize' }}>
                            {project.status || 'active'}
                          </Typography>
                        </Box>
                        {project.projectType && project.projectType !== 'professional' && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'capitalize' }}>
                            · {project.projectType}
                          </Typography>
                        )}
                        {project.collaborators && project.collaborators.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 'auto' }}>
                            <GroupIcon sx={{ fontSize: '0.8rem', color: 'text.secondary', opacity: 0.6 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                              {project.collaborators.length + 1}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Progress bar */}
                      {typeof project.progress === 'number' && project.progress > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              flexGrow: 1,
                              height: 4,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.divider, 0.4),
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${Math.min(project.progress, 100)}%`,
                                borderRadius: 2,
                                bgcolor: getPhaseColor(project.phase),
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, minWidth: 28 }}>
                            {project.progress}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))
            }
          </Grid>
        ) : (
          <Box 
            sx={{ 
              py: 6, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
              bgcolor: alpha(theme.palette.background.paper, 0.5),
              borderRadius: 2,
              border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`
            }}
          >
            <MovieIcon sx={{ fontSize: '3rem', mb: 2, opacity: 0.3 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              No projects available
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: '80%' }}>
              Projects you create or are assigned to will appear here.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Edit Task Modal */}
      {selectedTask && (
        <EditTaskModal
          open={isEditTaskModalOpen}
          onClose={() => { setIsEditTaskModalOpen(false); setSelectedTask(null); }}
          task={selectedTask}
          onTaskUpdate={handleDashboardTaskUpdate}
          projectId={selectedTask.project?._id || selectedTask.projectId}
        />
      )}
      
      {/* Edit Resource Modal */}
      {selectedResource && (
        <ResourceEditModal
          open={isEditResourceModalOpen}
          onClose={() => { setIsEditResourceModalOpen(false); setSelectedResource(null); }}
          resource={selectedResource}
          onResourceUpdate={(updatedResource) => {
            // Update the resource in the allResources array, preserving populated project object
            setAllResources(prevResources => 
              prevResources.map(resource => {
                const updatedId = updatedResource?._id || updatedResource?.id || selectedResource?._id;
                if (!updatedId) return resource;
                if (resource._id !== updatedId) return resource;
                const updatedProjectId = updatedResource.projectId;
                const preservedProject = typeof updatedProjectId === 'object' && updatedProjectId !== null
                  ? updatedProjectId
                  : resource.projectId || updatedProjectId;
                 const preservedProjectObj = typeof updatedResource.project === 'object' && updatedResource.project !== null
                   ? updatedResource.project
                   : resource.project || updatedResource.project;
                 return {
                   ...resource,
                   ...updatedResource,
                   projectId: preservedProject,
                   project: preservedProjectObj
                 };
               })
             );
            // Emit event to notify other components about the resource update
            const projectIdToEmit = selectedResource.projectId?._id || selectedResource.projectId;
            eventEmitter.emit(EVENTS.RESOURCE_UPDATED, {
              resource: updatedResource,
              projectId: projectIdToEmit
            });
            
            // Also set a flag in localStorage for ResourceManagement to check
            const updateFlag = {
              projectId: projectIdToEmit,
              resourceId: updatedResource._id || selectedResource._id
            };
            try {
              localStorage.setItem('resourceUpdateFlag', JSON.stringify(updateFlag));
            } catch (storageErr) {
              console.warn('Failed to write resourceUpdateFlag to localStorage:', storageErr);
            }
            setIsEditResourceModalOpen(false);
          }}
          projectId={selectedResource.projectId?._id || selectedResource.projectId}
        />
      )}
    </Box>
  );
}

export default Dashboard;
